import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import type { Session, User } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import { maybeTrackSignupCompleted } from "@/lib/funnel";

type Membership = {
  organization_id: string;
  role: string;
  organizations: {
    id: string;
    name: string;
    slug: string;
    logo_url: string | null;
    plan: string;
    trial_ends_at: string;
    onboarding_completed?: boolean;
  };
};

type AuthContextValue = {
  session: Session | null;
  user: User | null;
  loading: boolean;
  membershipsLoading: boolean;
  ready: boolean;
  memberships: Membership[];
  currentOrg: Membership["organizations"] | null;
  currentRole: string | null;
  isPlatformAdmin: boolean;
  setCurrentOrgId: (id: string) => void;
  refreshMemberships: () => Promise<void>;
  signOut: () => Promise<void>;
};

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

const STORAGE_KEY = "ppops.currentOrg";

// Lazy engagement: the Supabase auth listener + memberships load only run
// once the first `useAuth()` consumer mounts. Public marketing routes never
// call `useAuth()`, so `/` renders with zero Supabase requests.
let engagedGlobally = false;
const engageListeners = new Set<() => void>();
function requestEngage() {
  if (engagedGlobally) return;
  engagedGlobally = true;
  engageListeners.forEach((l) => l());
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [membershipsLoading, setMembershipsLoading] = useState(false);
  const [memberships, setMemberships] = useState<Membership[]>([]);
  const [currentOrgId, setCurrentOrgIdState] = useState<string | null>(null);
  const [isPlatformAdmin, setIsPlatformAdmin] = useState(false);
  const [engaged, setEngaged] = useState(false);

  useEffect(() => {
    if (engagedGlobally) { setEngaged(true); return; }
    const l = () => setEngaged(true);
    engageListeners.add(l);
    return () => { engageListeners.delete(l); };
  }, []);

  const loadMemberships = async (uid: string) => {
    setMembershipsLoading(true);
    try {
      const [{ data }, { data: pa }] = await Promise.all([
        supabase
          .from("organization_members")
          .select("organization_id, role, organizations(id, name, slug, logo_url, plan, trial_ends_at, onboarding_completed)")
          .eq("user_id", uid),
        supabase.from("platform_admins").select("user_id").eq("user_id", uid).maybeSingle(),
      ]);
      setIsPlatformAdmin(!!pa);
      const list = (data ?? []) as unknown as Membership[];
      setMemberships(list);
      if (list.length > 0) {
        const stored = typeof window !== "undefined" ? localStorage.getItem(STORAGE_KEY) : null;
        const exists = stored && list.find((m) => m.organization_id === stored);
        setCurrentOrgIdState(exists ? stored : list[0].organization_id);
      } else {
        setCurrentOrgIdState(null);
      }
    } finally {
      setMembershipsLoading(false);
    }
  };

  useEffect(() => {
    if (!engaged) return;

    let initialized = false;
    let hadSession = false;
    const { data: sub } = supabase.auth.onAuthStateChange((event, sess) => {
      setSession(sess);
      // If a previously-authenticated session disappears (token refresh failure
      // or expiry while the tab was idle), surface a clear message and bounce
      // to /login with a redirect-back param so the user lands where they were.
      if (initialized && hadSession && !sess && (event === "SIGNED_OUT" || event === "TOKEN_REFRESHED")) {
        if (typeof window !== "undefined") {
          const path = window.location.pathname + window.location.search;
          const isAuthRoute = /^\/(login|signup|forgot-password|reset-password|accept-invite|onboarding|$)/.test(window.location.pathname);
          if (!isAuthRoute) {
            // Use dynamic import-free toast through window event so we don't
            // create a hard dep cycle; sonner is already mounted in __root.
            import("sonner").then(({ toast }) => toast.error("Your session expired. Please sign in again."));
            import("./app-analytics").then(({ trackSessionExpired }) => trackSessionExpired(window.location.pathname));
            const next = encodeURIComponent(path);
            window.location.replace(`/login?next=${next}&redirect=${next}`);
            return;
          }
        }
      }
      if (sess?.user) {
        // Fire signup_completed exactly once per brand-new user. The helper
        // dedups via localStorage so Google OAuth round-trips, refreshes,
        // multiple tabs, and repeat SIGNED_IN events all collapse to one event.
        if (event === "SIGNED_IN" || event === "INITIAL_SESSION") {
          void maybeTrackSignupCompleted(sess.user);
        }
        hadSession = true;
        setMembershipsLoading(true);
        setTimeout(() => {
          loadMemberships(sess.user.id).finally(() => {
            if (!initialized) { initialized = true; setLoading(false); }
          });
        }, 0);
      } else {
        setMemberships([]);
        setCurrentOrgIdState(null);
        setMembershipsLoading(false);
        if (!initialized) { initialized = true; setLoading(false); }
      }
    });
    supabase.auth.getSession().then(({ data: { session: s } }) => {
      setSession(s);
      if (s?.user) {
        loadMemberships(s.user.id).finally(() => {
          if (!initialized) { initialized = true; setLoading(false); }
        });
      } else {
        if (!initialized) { initialized = true; setLoading(false); }
      }
    });
    return () => sub.subscription.unsubscribe();
  }, [engaged]);


  const setCurrentOrgId = (id: string) => {
    setCurrentOrgIdState(id);
    if (typeof window !== "undefined") localStorage.setItem(STORAGE_KEY, id);
  };

  const currentMembership = memberships.find((m) => m.organization_id === currentOrgId) ?? null;
  const ready = !loading && !membershipsLoading;

  const value: AuthContextValue = {
    session,
    user: session?.user ?? null,
    loading,
    membershipsLoading,
    ready,
    memberships,
    currentOrg: currentMembership?.organizations ?? null,
    currentRole: currentMembership?.role ?? null,
    isPlatformAdmin,
    setCurrentOrgId,
    refreshMemberships: async () => {
      if (session?.user) await loadMemberships(session.user.id);
    },
    signOut: async () => {
      await supabase.auth.signOut();
    },
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
  // First consumer to mount engages the Supabase auth engine.
  useEffect(() => { requestEngage(); }, []);
  return ctx;
}
